/**
 * NER-1276 — Single source of truth for contextual help + feature tours.
 *
 * Every help topic lives here. The registry derives, with no extra wiring:
 *   - inline `<HelpIcon topicKey="..." />` content (title + description)
 *   - route-aware sub-tours (TourContext matches `location.pathname` to `route`)
 *   - the Help Center menu in the Header (topics grouped by `feature`)
 *   - per-feature "completed" flags in localStorage (keyed by `feature`)
 *
 * HOW TO ADD A TOPIC: see src/help/README.md. In short — add an entry whose
 * key is `{feature}.{concept}`, point `titleKey`/`descriptionKey` at i18n keys
 * under `help.*` in es/en/pt, and (optionally) add a `tour` array. The
 * registry test validates that every i18n key resolves in all three locales.
 *
 * NOTE: this module is intentionally free of any import that transitively pulls
 * in `services/api.js` (axios is ESM and breaks CRA/jest). Keep it pure so
 * `registry.test.js` and `scripts/check-help-coverage.js` can read it cheaply.
 */

export const HELP_REGISTRY = {
  // ---- Dashboard --------------------------------------------------------
  'dashboard.overview': {
    feature: 'dashboard',
    // The dashboard is the landing route, mounted at "/" (App.js), not
    // "/dashboard" (which doesn't exist). Login navigates to "/", so the
    // auto-tour must match "/" or it never fires. NER-1276 QA fix.
    route: '/',
    titleKey: 'help.dashboard.overview.title',
    descriptionKey: 'help.dashboard.overview.description',
    // NER-1563: Citame onboarding. The Resto "tools panel" (.dock-panel) step was
    // removed — Citame admin is dock-free. Targets are limited to elements that
    // are always present on the dashboard route so no step lands on a missing node.
    tour: [
      {
        target: '.activity-bar',
        titleKey: 'help.dashboard.overview.steps.0.title',
        contentKey: 'help.dashboard.overview.steps.0.content',
        placement: 'right',
      },
      {
        target: '.dashboard-stats',
        titleKey: 'help.dashboard.overview.steps.1.title',
        contentKey: 'help.dashboard.overview.steps.1.content',
        placement: 'bottom',
      },
      {
        target: '.quick-actions',
        titleKey: 'help.dashboard.overview.steps.2.title',
        contentKey: 'help.dashboard.overview.steps.2.content',
        placement: 'top',
      },
      {
        target: '.header-new',
        titleKey: 'help.dashboard.overview.steps.3.title',
        contentKey: 'help.dashboard.overview.steps.3.content',
        placement: 'bottom',
      },
    ],
  },
  'dashboard.stats': {
    feature: 'dashboard',
    titleKey: 'help.dashboard.stats.title',
    descriptionKey: 'help.dashboard.stats.description',
  },
  'dashboard.quickActions': {
    feature: 'dashboard',
    titleKey: 'help.dashboard.quickActions.title',
    descriptionKey: 'help.dashboard.quickActions.description',
  },

  // ---- Agenda (NER-1563 — Citame onboarding) ---------------------------
  'agenda.overview': {
    feature: 'agenda',
    route: '/agenda',
    titleKey: 'help.agenda.overview.title',
    descriptionKey: 'help.agenda.overview.description',
    tour: [
      {
        target: '.agenda-page',
        titleKey: 'help.agenda.overview.steps.0.title',
        contentKey: 'help.agenda.overview.steps.0.content',
        placement: 'bottom',
      },
    ],
  },

  // ---- Professionals (NER-1563 — Citame onboarding) --------------------
  'professionals.overview': {
    feature: 'professionals',
    route: '/professionals',
    titleKey: 'help.professionals.overview.title',
    descriptionKey: 'help.professionals.overview.description',
    tour: [
      {
        target: '.list-page',
        titleKey: 'help.professionals.overview.steps.0.title',
        contentKey: 'help.professionals.overview.steps.0.content',
        placement: 'bottom',
      },
    ],
  },

  // ---- Services (NER-1563 — Citame onboarding) -------------------------
  'services.overview': {
    feature: 'services',
    route: '/services',
    titleKey: 'help.services.overview.title',
    descriptionKey: 'help.services.overview.description',
    tour: [
      {
        target: '.list-page',
        titleKey: 'help.services.overview.steps.0.title',
        contentKey: 'help.services.overview.steps.0.content',
        placement: 'bottom',
      },
    ],
  },

  // ---- Conversations / WhatsApp (NER-1563 — Citame onboarding) ---------
  'conversations.overview': {
    feature: 'conversations',
    route: '/conversations',
    titleKey: 'help.conversations.overview.title',
    descriptionKey: 'help.conversations.overview.description',
    tour: [
      {
        target: '.conversations-page',
        titleKey: 'help.conversations.overview.steps.0.title',
        contentKey: 'help.conversations.overview.steps.0.content',
        placement: 'bottom',
      },
    ],
  },

  // ---- Metrics / Métricas por vertical (NER-2785) -----------------------
  'metrics.overview': {
    feature: 'metrics',
    route: '/metrics',
    titleKey: 'help.metrics.overview.title',
    descriptionKey: 'help.metrics.overview.description',
    tour: [
      {
        target: '.metrics-page',
        titleKey: 'help.metrics.overview.steps.0.title',
        contentKey: 'help.metrics.overview.steps.0.content',
        placement: 'bottom',
      },
      {
        target: '.metrics-tabs',
        titleKey: 'help.metrics.overview.steps.1.title',
        contentKey: 'help.metrics.overview.steps.1.content',
        placement: 'bottom',
      },
    ],
  },

  // ---- Subscription / Plan (NER-1279) — OWNER ---------------------------
  'subscription.overview': {
    feature: 'subscription',
    route: '/subscription',
    titleKey: 'help.subscription.overview.title',
    descriptionKey: 'help.subscription.overview.description',
    tour: [
      {
        target: '.subscription-page',
        titleKey: 'help.subscription.overview.steps.0.title',
        contentKey: 'help.subscription.overview.steps.0.content',
        placement: 'bottom',
      },
      {
        target: '.plan-card',
        titleKey: 'help.subscription.overview.steps.1.title',
        contentKey: 'help.subscription.overview.steps.1.content',
        placement: 'bottom',
      },
      {
        target: '.usage-section',
        titleKey: 'help.subscription.overview.steps.2.title',
        contentKey: 'help.subscription.overview.steps.2.content',
        placement: 'top',
      },
      {
        target: '.plan-card',
        titleKey: 'help.subscription.overview.steps.3.title',
        contentKey: 'help.subscription.overview.steps.3.content',
        placement: 'bottom',
      },
    ],
  },
  'subscription.tiers': {
    feature: 'subscription',
    titleKey: 'help.subscription.tiers.title',
    descriptionKey: 'help.subscription.tiers.description',
  },
  'subscription.limits': {
    feature: 'subscription',
    titleKey: 'help.subscription.limits.title',
    descriptionKey: 'help.subscription.limits.description',
  },
  'subscription.overage': {
    feature: 'subscription',
    titleKey: 'help.subscription.overage.title',
    descriptionKey: 'help.subscription.overage.description',
  },
  'subscription.billingCycle': {
    feature: 'subscription',
    titleKey: 'help.subscription.billingCycle.title',
    descriptionKey: 'help.subscription.billingCycle.description',
  },

  // ---- Settings / Business config (NER-1563 — Citame onboarding) -------
  // Covers business config (hours, reminders, WhatsApp) + coverages/convenios
  // (NER-1778/NER-1783). Replaces the Resto-only paymentMethods + autoClose
  // ("cierre automático de mesas") topics, which don't apply to Citame.
  'settings.overview': {
    feature: 'settings',
    route: '/settings',
    titleKey: 'help.settings.overview.title',
    descriptionKey: 'help.settings.overview.description',
    tour: [
      {
        target: '.settings-page',
        titleKey: 'help.settings.overview.steps.0.title',
        contentKey: 'help.settings.overview.steps.0.content',
        placement: 'bottom',
      },
      {
        target: '.settings-page',
        titleKey: 'help.settings.overview.steps.1.title',
        contentKey: 'help.settings.overview.steps.1.content',
        placement: 'bottom',
      },
    ],
  },
};

/** Order in which features appear in the Help Center menu. Unknown features
 *  are appended alphabetically after these. */
const FEATURE_ORDER = [
  'dashboard',
  'agenda',
  'professionals',
  'services',
  'conversations',
  'metrics',
  'settings',
  'subscription',
];

/** Returns the topic entry for a key, or undefined. */
export function getTopic(topicKey) {
  return HELP_REGISTRY[topicKey];
}

/** All topic keys (used by tests + the coverage lint script). */
export function getAllTopicKeys() {
  return Object.keys(HELP_REGISTRY);
}

/**
 * Pure route → feature resolver for auto-triggered sub-tours.
 * Returns the `feature` of the registry entry whose `route` best matches
 * `pathname` (longest matching route wins), only considering entries that
 * actually define a `tour`. Returns null when nothing matches.
 */
export function getAutoTourFeature(pathname) {
  if (!pathname) return null;
  let best = null;
  let bestLen = -1;
  for (const entry of Object.values(HELP_REGISTRY)) {
    if (!entry.route || !Array.isArray(entry.tour) || entry.tour.length === 0) continue;
    const matches = pathname === entry.route || pathname.startsWith(`${entry.route}/`);
    if (matches && entry.route.length > bestLen) {
      best = entry.feature;
      bestLen = entry.route.length;
    }
  }
  return best;
}

/**
 * Returns the tour steps for a feature (first registry entry of that feature
 * that defines a non-empty `tour`), or an empty array.
 */
export function getTourForFeature(feature) {
  for (const entry of Object.values(HELP_REGISTRY)) {
    if (entry.feature === feature && Array.isArray(entry.tour) && entry.tour.length > 0) {
      return entry.tour;
    }
  }
  return [];
}

/** True if the feature has at least one defined tour. */
export function featureHasTour(feature) {
  return getTourForFeature(feature).length > 0;
}

/**
 * Topics grouped by feature for the Help Center menu, in FEATURE_ORDER.
 * Returns: [{ feature, topics: [{ topicKey, ...entry }] }]
 */
export function getHelpCenterGroups() {
  const byFeature = {};
  for (const [topicKey, entry] of Object.entries(HELP_REGISTRY)) {
    if (!byFeature[entry.feature]) byFeature[entry.feature] = [];
    byFeature[entry.feature].push({ topicKey, ...entry });
  }
  const features = Object.keys(byFeature).sort((a, b) => {
    const ia = FEATURE_ORDER.indexOf(a);
    const ib = FEATURE_ORDER.indexOf(b);
    if (ia === -1 && ib === -1) return a.localeCompare(b);
    if (ia === -1) return 1;
    if (ib === -1) return -1;
    return ia - ib;
  });
  return features.map((feature) => ({ feature, topics: byFeature[feature] }));
}
